import { supabase } from '@/lib/supabase';
import { toDashboardUiError } from '@/lib/dashboard-errors';
import type {
  BootstrapPrebuiltToolsRequest,
  BootstrapPrebuiltToolsResponse,
  ResolveToolRuntimeRequest,
  TenantPrebuiltToolRecord,
  ToolRuntimePublicState,
} from '@shared/types/prebuilt-tools';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isTenantPrebuiltToolRecord = (value: unknown): value is TenantPrebuiltToolRecord => (
  isRecord(value)
  && typeof value.id === 'string'
  && typeof value.tenant_id === 'string'
  && typeof value.tool_key === 'string'
  && typeof value.skill_key === 'string'
  && typeof value.template_key === 'string'
  && typeof value.pack_key === 'string'
  && typeof value.pack_version === 'string'
  && (value.installed_flow_id === null || typeof value.installed_flow_id === 'string')
  && (value.workspace_dashboard_id === null || typeof value.workspace_dashboard_id === 'string')
  && typeof value.status === 'string'
  && isRecord(value.config)
  && typeof value.created_at === 'string'
  && typeof value.updated_at === 'string'
);

const isBootstrapPrebuiltToolsResponse = (value: unknown): value is BootstrapPrebuiltToolsResponse => (
  isRecord(value)
  && Array.isArray(value.tools)
  && value.tools.every((entry) => isTenantPrebuiltToolRecord(entry))
);

const isToolRuntimePublicState = (value: unknown): value is ToolRuntimePublicState => {
  if (!isRecord(value) || typeof value.status !== 'string') return false;
  if (typeof value.toolKey !== 'string') return false;
  if (value.dashboardId !== null && typeof value.dashboardId !== 'string') return false;
  if (value.workspaceTitle !== null && typeof value.workspaceTitle !== 'string') return false;
  if (value.flowId !== null && typeof value.flowId !== 'string') return false;

  switch (value.status) {
    case 'ready_immediately':
      return typeof value.ctaLabel === 'string';
    case 'missing_one_thing':
      return (
        isRecord(value.requirement)
        && typeof value.requirement.kind === 'string'
        && typeof value.requirement.title === 'string'
        && typeof value.requirement.description === 'string'
        && typeof value.requirement.ctaLabel === 'string'
      );
    case 'running':
      return (
        typeof value.executionId === 'string'
        && (value.executionStatus === 'pending' || value.executionStatus === 'running')
      );
    case 'blocked':
      return (
        typeof value.executionId === 'string'
        && value.executionStatus === 'paused'
      );
    case 'workspace_ready':
      return value.executionId === null || typeof value.executionId === 'string';
    default:
      return false;
  }
};

export const bootstrapPrebuiltTools = async (
  request?: BootstrapPrebuiltToolsRequest,
): Promise<BootstrapPrebuiltToolsResponse> => {
  const { data, error } = await supabase.functions.invoke('bootstrap-prebuilt-tools', {
    body: request ?? {},
  });

  if (error) {
    throw await toDashboardUiError(error, 'Impossible de préparer les outils Domino pour le moment.');
  }

  if (!isBootstrapPrebuiltToolsResponse(data)) {
    throw new Error('Réponse invalide de bootstrap-prebuilt-tools.');
  }

  return data;
};

export const resolveToolRuntime = async (
  request: ResolveToolRuntimeRequest,
): Promise<ToolRuntimePublicState> => {
  const { data, error } = await supabase.functions.invoke('resolve-tool-runtime', {
    body: request,
  });

  if (error) {
    throw await toDashboardUiError(error, 'Impossible de préparer ce tool pour le moment.');
  }

  if (!isToolRuntimePublicState(data)) {
    throw new Error('Réponse invalide de resolve-tool-runtime.');
  }

  return data;
};
